import React from 'react';
import { ArrowUpDown, Filter, Search, X } from '../../../bravosIcons';
import { LibraryFilterPopover } from '../components/LibraryFilterPopover';
import type { LibraryWorkoutsApi } from '../../hooks/useLibraryWorkouts';
import type { WorkoutDurationBucket } from '../../types/library';

type WorkoutsToolbarDesktopProps = {
  wk: LibraryWorkoutsApi;
  /** When omitted, toolbar always renders (caller gates by tab). */
  activeTab?: string;
};

/** Desktop Library — Workouts tab toolbar (§5). */
export function WorkoutsToolbarDesktop({ wk, activeTab }: WorkoutsToolbarDesktopProps) {
  if (activeTab != null && activeTab !== 'workouts') return null;
  if (wk.libWorkoutsEmptyVariant === 'true-empty') return null;

  return (
    <div className="lib-toolbar">
      <div className="lib-search">
        <Search />
        <input
          type="text"
          value={wk.workoutNameFilter}
          onChange={(e) => wk.setWorkoutNameFilter(e.target.value)}
          placeholder="Search workout name…"
          aria-label="Search workouts by name"
        />
      </div>

      <div className="relative" ref={wk.libMuscleChipRef}>
        <button
          type="button"
          className={`lib-chip ${(wk.libOpenChip === 'muscle' || wk.libMuscleFilter.size > 0) ? 'is-active' : ''}`}
          onClick={() => wk.setLibOpenChip(wk.libOpenChip === 'muscle' ? null : 'muscle')}
          aria-haspopup="dialog"
          aria-expanded={wk.libOpenChip === 'muscle'}
        >
          <Filter />
          Muscle group
          {wk.libMuscleFilter.size > 0 && (
            <span className="lib-chip-count-pill lib-tnum">{wk.libMuscleFilter.size}</span>
          )}
        </button>
        {wk.libOpenChip === 'muscle' && (
          <LibraryFilterPopover
            options={wk.libMuscleFilterOptions}
            selected={wk.libMuscleFilter}
            onChange={(next) => wk.setLibMuscleFilter(next as Set<string>)}
            onClose={() => wk.setLibOpenChip(null)}
            emptyMessage="No muscle groups in your workouts yet."
            ariaLabel="Muscle group filter"
          />
        )}
      </div>

      <div className="relative" ref={wk.libEquipmentChipRef}>
        <button
          type="button"
          className={`lib-chip ${(wk.libOpenChip === 'equipment' || wk.libEquipmentFilter.size > 0) ? 'is-active' : ''}`}
          onClick={() => wk.setLibOpenChip(wk.libOpenChip === 'equipment' ? null : 'equipment')}
          aria-haspopup="dialog"
          aria-expanded={wk.libOpenChip === 'equipment'}
        >
          <Filter />
          Equipment
          {wk.libEquipmentFilter.size > 0 && (
            <span className="lib-chip-count-pill lib-tnum">{wk.libEquipmentFilter.size}</span>
          )}
        </button>
        {wk.libOpenChip === 'equipment' && (
          <LibraryFilterPopover
            options={wk.libEquipmentFilterOptions}
            selected={wk.libEquipmentFilter}
            onChange={(next) => wk.setLibEquipmentFilter(next as Set<string>)}
            onClose={() => wk.setLibOpenChip(null)}
            emptyMessage="No equipment values in your workouts yet."
            ariaLabel="Equipment filter"
          />
        )}
      </div>

      <div className="relative" ref={wk.libDurationChipRef}>
        <button
          type="button"
          className={`lib-chip ${(wk.libOpenChip === 'duration' || wk.libDurationActive) ? 'is-active' : ''}`}
          onClick={() => wk.setLibOpenChip(wk.libOpenChip === 'duration' ? null : 'duration')}
          aria-haspopup="dialog"
          aria-expanded={wk.libOpenChip === 'duration'}
        >
          <Filter />
          Duration
          {wk.libDurationFilter.size > 0 && (
            <span className="lib-chip-count-pill lib-tnum">{wk.libDurationFilter.size}</span>
          )}
        </button>
        {wk.libOpenChip === 'duration' && (
          <LibraryFilterPopover
            options={[
              { id: 'lt30', label: '< 30 min', matchCount: wk.libDurationMatchCounts.lt30 },
              { id: '30to60', label: '30–60 min', matchCount: wk.libDurationMatchCounts['30to60'] },
              { id: 'gt60', label: '> 60 min', matchCount: wk.libDurationMatchCounts.gt60 },
            ]}
            selected={wk.libDurationFilter as Set<string>}
            onChange={(next) => wk.setLibDurationFilter(next as Set<WorkoutDurationBucket>)}
            onClose={() => wk.setLibOpenChip(null)}
            ariaLabel="Duration filter"
          />
        )}
      </div>

      <span className="lib-chip-divider" aria-hidden="true" />

      <div className="relative" ref={wk.libSortChipRef}>
        <button
          type="button"
          className={`lib-chip ${(wk.libOpenChip === 'sort' || !wk.libSortIsDefault) ? 'is-active' : ''}`}
          onClick={() => wk.setLibOpenChip(wk.libOpenChip === 'sort' ? null : 'sort')}
          aria-haspopup="listbox"
          aria-expanded={wk.libOpenChip === 'sort'}
        >
          <ArrowUpDown />
          <span className="lib-sort-label">{wk.libSortIsDefault ? 'Sort' : `Sort · ${wk.libCurrentSort.label}`}</span>
        </button>
        {wk.libOpenChip === 'sort' && (
          <div className="lib-popover lib-sort-popover" role="listbox" onKeyDown={(e) => { if (e.key === 'Escape') wk.setLibOpenChip(null); }}>
            {wk.LIB_SORT_OPTIONS.map((opt) => (
              <label key={opt.id} className={`lib-popover-row ${wk.libCurrentSort.id === opt.id ? 'is-checked' : ''}`}>
                <input
                  type="radio"
                  name="lib-sort"
                  checked={wk.libCurrentSort.id === opt.id}
                  onChange={() => {
                    wk.setWorkoutSortColumn(opt.column);
                    wk.setWorkoutSortDirection(opt.direction);
                    wk.setLibOpenChip(null);
                  }}
                />
                <span className="lib-popover-row-label">{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {wk.libWorkoutsHasActiveControls && (
        <button
          type="button"
          className="lib-clear-all-chip"
          onClick={wk.handleClearAllWorkouts}
          aria-label="Clear all filters, search, and sort"
        >
          <X />
          Clear all
          <span className="lib-clear-all-chip-count lib-tnum">{wk.libWorkoutsAppliedCount}</span>
        </button>
      )}

      <div className="lib-results-meta">
        <strong className="lib-tnum">{wk.filteredAndSortedWorkouts.length}</strong>{' '}
        {wk.filteredAndSortedWorkouts.length === 1 ? 'result' : 'results'}
      </div>
    </div>
  );
}
